/**
 * DmChatBubble — Facebook-style floating chat bubble anchored to the
 * bottom-right of the viewport. Shows a compact list of recent DM
 * conversations with unread badges. Clicking a conversation jumps to
 * /messages/:id.
 *
 * Silently no-ops for unauthenticated users. Refreshes when the panel
 * is opened.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { MessageCircle, X, Loader2, Search } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

const DmChatBubble = () => {
  const { API, user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);

  // Poll unread count every 45s so the badge stays fresh even when closed.
  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/dm/unread-count`);
        if (alive) setUnread(data?.unread || 0);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 45000);
    return () => { alive = false; clearInterval(t); };
  }, [API, user]);

  // Load conversations lazily when the panel opens.
  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    axios.get(`${API}/dm/conversations`)
      .then((r) => { setConvs(r.data?.conversations || []); setUnread(r.data?.unread_count || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, API, user]);

  if (!user) return null;

  return (
    <>
      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-20 right-5 z-50 w-80 max-w-[calc(100vw-2.5rem)] rounded-2xl border border-border bg-card shadow-2xl overflow-hidden"
          data-testid="dm-chat-panel"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-primary text-primary-foreground">
            <div className="font-heading font-extrabold text-sm inline-flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> Messages
              {unread > 0 && (
                <span className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold h-4 min-w-[16px] px-1">
                  {unread}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Link
                to="/messages"
                className="text-[10px] uppercase tracking-widest font-bold opacity-90 hover:opacity-100"
                onClick={() => setOpen(false)}
                data-testid="dm-bubble-open-inbox"
              >
                Inbox
              </Link>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="h-6 w-6 rounded-full hover:bg-white/20 inline-flex items-center justify-center"
                aria-label="Close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-xs text-muted-foreground inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> loading…
              </div>
            ) : convs.length === 0 ? (
              <div className="p-6 text-center text-xs text-muted-foreground">
                <Search className="w-5 h-5 mx-auto mb-1 opacity-50" />
                No conversations yet.
                <div className="mt-2">
                  <Link
                    to="/directory"
                    onClick={() => setOpen(false)}
                    className="text-primary font-bold hover:underline"
                    data-testid="dm-bubble-directory-link"
                  >
                    Find members →
                  </Link>
                </div>
              </div>
            ) : (
              <ul>
                {convs.slice(0, 12).map((c) => (
                  <li key={c.id}>
                    <Link
                      to={`/messages/${c.id}`}
                      onClick={() => setOpen(false)}
                      className={`flex items-start gap-2.5 px-4 py-2.5 hover:bg-muted transition-colors border-b border-border last:border-b-0 ${
                        c.unread ? 'bg-primary/5' : ''
                      }`}
                      data-testid={`dm-bubble-conv-${c.id}`}
                    >
                      <div className="h-9 w-9 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[11px] font-bold flex-shrink-0 overflow-hidden">
                        {c.peer?.avatar_url
                          ? <img src={c.peer.avatar_url} alt="" className="h-full w-full object-cover" />
                          : (c.peer?.name || '?').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm truncate ${c.unread ? 'font-extrabold' : 'font-semibold'}`}>
                            {c.peer?.name || 'Member'}
                          </span>
                          <span className="text-[10px] text-muted-foreground whitespace-nowrap flex-shrink-0">
                            {c.last_message_at ? (() => {
                              try { return formatDistanceToNow(parseISO(c.last_message_at), { addSuffix: false }); }
                              catch { return ''; }
                            })() : ''}
                          </span>
                        </div>
                        <div className={`text-xs truncate ${c.unread ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>
                          {c.last_snippet || 'No messages yet.'}
                        </div>
                      </div>
                      {c.unread && (
                        <span className="mt-1 h-2 w-2 rounded-full bg-primary flex-shrink-0" aria-label="unread" />
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Bubble */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open direct messages"
        data-testid="dm-chat-bubble"
        className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-xl hover:scale-105 active:scale-95 transition-transform flex items-center justify-center"
      >
        <MessageCircle className="w-6 h-6" />
        {unread > 0 && !open && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold h-5 min-w-[20px] px-1 ring-2 ring-background">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
    </>
  );
};

export default DmChatBubble;
